export type TrustedInteractionType =
  | 'click'
  | 'pointerdown'
  | 'pointermove'
  | 'scroll'
  | 'touchstart';

// Version the key so future changes to the trust rules do not silently reuse
// old decisions. The user ID suffix prevents one trusted account on a shared
// device from trusting every other account.
const LEGITIMATE_USER_STORAGE_KEY_PREFIX = 'madpaws:analytics:legitimate-user:v1';
const RECENT_TRUSTED_INTERACTION_STORAGE_KEY = 'madpaws:analytics:recent-trusted-interaction:v1';
// A recent interaction is useful for reducing repeat observation on the next
// visit, but it expires so old browser decisions are not trusted forever.
const RECENT_TRUSTED_INTERACTION_TTL_MS = 24 * 60 * 60 * 1000;
const TRUSTED_INTERACTION_EVENTS: TrustedInteractionType[] = [
  'click',
  'pointerdown',
  'pointermove',
  'scroll',
  'touchstart',
];

let analyticsUserId: string | null = null;
let hasObservedTrustedInteraction = false;
let trustedInteractionType: TrustedInteractionType | null = null;
let hasInstalledInteractionListeners = false;

type StoredTrustedInteraction = {
  observedAt: number;
  type: TrustedInteractionType;
};

const getLegitimateUserStorageKey = (userId: string): string =>
  `${LEGITIMATE_USER_STORAGE_KEY_PREFIX}:${userId}`;

export const hasLocalLegitimateUser = (): boolean => {
  // Never use one anonymous browser marker as a global allow-list. Without an
  // authenticated ID, there is no safe account to which the marker belongs.
  if (typeof window === 'undefined' || !analyticsUserId) {
    return false;
  }

  try {
    return window.localStorage.getItem(getLegitimateUserStorageKey(analyticsUserId)) === 'true';
  } catch {
    // Local storage can be unavailable in privacy-restricted browser contexts.
    return false;
  }
};

const getRecentTrustedInteraction = (): StoredTrustedInteraction | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedInteraction = window.localStorage.getItem(RECENT_TRUSTED_INTERACTION_STORAGE_KEY);

    if (!storedInteraction) {
      return null;
    }

    const parsedInteraction = JSON.parse(storedInteraction) as Partial<StoredTrustedInteraction>;
    const observedAt = parsedInteraction.observedAt;
    const interactionType = parsedInteraction.type;
    const isValidType = TRUSTED_INTERACTION_EVENTS.includes(
      interactionType as TrustedInteractionType
    );
    const isRecent =
      typeof observedAt === 'number' &&
      Date.now() - observedAt <= RECENT_TRUSTED_INTERACTION_TTL_MS;

    if (!isRecent || !isValidType) {
      window.localStorage.removeItem(RECENT_TRUSTED_INTERACTION_STORAGE_KEY);
      return null;
    }

    return {
      observedAt: observedAt as number,
      type: interactionType as TrustedInteractionType,
    };
  } catch {
    return null;
  }
};

const saveRecentTrustedInteraction = (type: TrustedInteractionType): void => {
  if (typeof window === 'undefined' || analyticsUserId) {
    return;
  }

  try {
    const interaction: StoredTrustedInteraction = {
      observedAt: Date.now(),
      type,
    };

    window.localStorage.setItem(
      RECENT_TRUSTED_INTERACTION_STORAGE_KEY,
      JSON.stringify(interaction)
    );
  } catch {
    // Local storage can be unavailable in privacy-restricted browser contexts.
  }
};

const handleTrustedInteraction = (event: Event): void => {
  // Synthetic browser events normally have isTrusted=false. This still is not
  // a perfect bot detector, so the result is recorded for observation only.
  if (!event.isTrusted || hasObservedTrustedInteraction) {
    return;
  }

  hasObservedTrustedInteraction = true;
  trustedInteractionType = event.type as TrustedInteractionType;
  saveRecentTrustedInteraction(trustedInteractionType);

  TRUSTED_INTERACTION_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, handleTrustedInteraction, true);
  });
  hasInstalledInteractionListeners = false;
};

const removeTrustedInteractionListeners = (): void => {
  if (typeof window === 'undefined' || !hasInstalledInteractionListeners) {
    return;
  }

  TRUSTED_INTERACTION_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, handleTrustedInteraction, true);
  });

  hasInstalledInteractionListeners = false;
};

/**
 * Starts observation only. These events are engagement signals, not proof that
 * a session is human. Pointer and touch events cover mobile browsers.
 */
export const initializeTrustedInteractionTracking = (): void => {
  // A previously verified user is already trusted for this browser account;
  // do not add interaction listeners or include interaction-based blocking
  // logic for that user.
  if (
    typeof window === 'undefined' ||
    hasInstalledInteractionListeners ||
    hasLocalLegitimateUser() ||
    Boolean(analyticsUserId) ||
    Boolean(getRecentTrustedInteraction())
  ) {
    return;
  }

  hasInstalledInteractionListeners = true;

  TRUSTED_INTERACTION_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleTrustedInteraction, {
      capture: true,
      passive: true,
    });
  });
};

/**
 * Sets the authenticated user used to scope the local legitimate-user marker.
 * This is intentionally not inferred from browser-controlled values.
 */
export const setAnalyticsUserId = (userId?: number | string | null): void => {
  if (userId !== undefined && userId !== null) {
    analyticsUserId = String(userId);

    // Authentication can complete after the provider first mounts. Remove
    // listeners if the newly known account is already locally trusted.
    if (hasLocalLegitimateUser()) {
      removeTrustedInteractionListeners();
    }
  }
};

/**
 * Saves an explicit legitimate-user decision for a specific authenticated
 * account. Callers should invoke this only after a sitter has been verified;
 * this function is not called automatically from login or page loading.
 */
export const markUserAsLegitimate = (userId: number | string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(getLegitimateUserStorageKey(String(userId)), 'true');

    if (analyticsUserId === String(userId)) {
      removeTrustedInteractionListeners();
    }
  } catch {
    // Local storage can be unavailable in privacy-restricted browser contexts.
  }
};

const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  // User-agent detection covers older mobile browsers; touch capability covers
  // embedded/mobile browsers whose user agent is not conventional.
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    'ontouchstart' in window
  );
};

/**
 * Builds the metadata added to page and track events. The fields intentionally
 * describe the proposed decision rather than enforce it, so the team can
 * measure false positives before Segment loading is ever blocked.
 */
export const getBotTrafficObservationProperties = (
  wafTrafficMetadata: WafTrafficMetadata
): Record<string, unknown> => {
  const localLegitimateUser = hasLocalLegitimateUser();
  const hasUserId = Boolean(analyticsUserId);
  const recentTrustedInteraction = getRecentTrustedInteraction();
  const trustedInteractionObserved =
    hasObservedTrustedInteraction || Boolean(recentTrustedInteraction);

  // These fields are deliberately added to page and track events instead of
  // changing Segment behavior. This lets us compare the proposed rule with
  // real conversions before enabling any blocking.
  return {
    bot_detection_mode: 'observe_only',
    has_local_legitimate_user: localLegitimateUser,
    has_user_id: hasUserId,
    ...wafTrafficMetadata,
    has_recent_trusted_interaction: Boolean(recentTrustedInteraction),
    trusted_interaction_observed: trustedInteractionObserved,
    trusted_interaction_type: trustedInteractionType || recentTrustedInteraction?.type || null,
    trusted_interaction_source: hasObservedTrustedInteraction
      ? 'current_session'
      : recentTrustedInteraction
      ? 'local_storage'
      : null,
    is_mobile_device: isMobileDevice(),
    // This is an observation of the proposed future rule; it does not block.
    would_block_segment:
      !localLegitimateUser &&
      !hasUserId &&
      wafTrafficMetadata.waf_suspicious_traffic &&
      !trustedInteractionObserved,
  };
};
import type { WafTrafficMetadata } from '~/utils/wafSuspiciousTraffic';
